import { activateSieveScript, createSieveScript, deleteSieveScript, getSieveScript, listSieveScripts, updateSieveScript, } from "@apps/client-sdk";
import { showConfirmDialog } from "../../confirmDialog.js";
import { el } from "./dom.js";
export function createScriptWorkspace(options) {
    const layout = el("div", "sieve-layout mt");
    const listColumn = el("div", "sieve-list-column");
    listColumn.append(el("h3", undefined, "Scripts"));
    const scriptList = el("ul", "sieve-script-list");
    listColumn.append(scriptList);
    const newRow = el("div", "sieve-new-script mt");
    const newNameInput = el("input");
    newNameInput.type = "text";
    newNameInput.placeholder = "new-script-name";
    newNameInput.className = "input";
    const createBtn = el("button", "btn btn-secondary btn-sm", "New script");
    createBtn.type = "button";
    newRow.append(newNameInput, createBtn);
    listColumn.append(newRow);
    const editorColumn = el("div", "sieve-editor-column");
    editorColumn.append(el("h3", undefined, "Editor"));
    if (options.hint) {
        editorColumn.append(el("p", "hint", options.hint));
    }
    const editorTitle = el("p", "muted", "Select a script to edit.");
    editorColumn.append(editorTitle, options.editorSlot);
    const actionRow = el("div", "inline-add mt");
    const saveBtn = el("button", "btn btn-primary btn-sm", "Save");
    saveBtn.type = "button";
    saveBtn.disabled = true;
    const activateBtn = el("button", "btn btn-secondary btn-sm", "Activate");
    activateBtn.type = "button";
    activateBtn.disabled = true;
    const deleteBtn = el("button", "btn btn-secondary btn-sm", "Delete");
    deleteBtn.type = "button";
    deleteBtn.disabled = true;
    actionRow.append(saveBtn, activateBtn, deleteBtn);
    editorColumn.append(actionRow);
    layout.append(listColumn, editorColumn);
    let activeScript = null;
    let selectedScript = null;
    let loading = false;
    let content = "";
    let dirty = false;
    const getEditorValue = () => {
        const ta = options.editorSlot.querySelector("textarea.sieve-source-editor");
        if (ta)
            return ta.value;
        return content;
    };
    const markDirty = () => {
        if (selectedScript)
            dirty = true;
    };
    const markClean = () => {
        dirty = false;
    };
    const isDirty = () => dirty && !!selectedScript;
    const confirmDiscardIfDirty = async () => {
        if (!isDirty())
            return true;
        return showConfirmDialog(options.root, {
            title: "Discard unsaved changes?",
            message: "You have unsaved edits. Leave without saving?",
            confirmLabel: "Discard",
            cancelLabel: "Stay",
            danger: true,
        });
    };
    const updateEditorLock = () => {
        const locked = !selectedScript;
        options.editorSlot.classList.toggle("sieve-editor-locked", locked);
        const ta = options.editorSlot.querySelector("textarea.sieve-source-editor");
        if (ta) {
            ta.disabled = locked;
            ta.readOnly = locked;
        }
    };
    const setEditorValue = (value, opts) => {
        content = value;
        const ta = options.editorSlot.querySelector("textarea.sieve-source-editor");
        if (ta) {
            ta.value = value;
        }
        updateEditorLock();
        if (opts?.asBaseline) {
            markClean();
        }
        options.onScriptContent?.(selectedScript, value);
    };
    const setBusy = (busy) => {
        loading = busy;
        createBtn.disabled = busy;
        saveBtn.disabled = busy || !selectedScript;
        activateBtn.disabled = busy || !selectedScript;
        deleteBtn.disabled = busy || !selectedScript;
        newNameInput.disabled = busy;
    };
    const renderScriptList = (scripts) => {
        scriptList.replaceChildren();
        if (activeScript) {
            scriptList.append(el("p", "hint", `Active script: ${activeScript}`));
        }
        if (scripts.length === 0) {
            scriptList.append(el("li", "muted", "No scripts on the server."));
            return;
        }
        for (const name of scripts.sort((a, b) => a.localeCompare(b))) {
            const item = el("li", "sieve-script-item");
            const btn = el("button", `btn btn-secondary btn-sm sieve-script-btn${selectedScript === name ? " active" : ""}`, name);
            btn.type = "button";
            if (activeScript === name) {
                btn.append(document.createTextNode(" "));
                btn.append(el("span", "sieve-active-badge", "active"));
            }
            btn.addEventListener("click", () => {
                void selectScript(name);
            });
            item.append(btn);
            scriptList.append(item);
        }
    };
    const refresh = async () => {
        setBusy(true);
        try {
            const data = await listSieveScripts(options.storage);
            activeScript = data.active;
            renderScriptList(data.scripts);
        }
        catch (error) {
            options.showStatus(error instanceof Error ? error.message : "Failed to load scripts", "error");
        }
        finally {
            setBusy(false);
        }
    };
    const clearSelection = () => {
        selectedScript = null;
        markClean();
        setEditorValue("", { asBaseline: true });
        editorTitle.textContent = "Select a script to edit.";
        options.onSelectionChange?.(null);
        setBusy(false);
    };
    const selectScript = async (name) => {
        if (loading)
            return;
        if (selectedScript === name)
            return;
        if (!(await confirmDiscardIfDirty()))
            return;
        setBusy(true);
        try {
            const script = await getSieveScript(options.storage, name);
            selectedScript = script.name;
            // Unlock / enable the visual editor before loading content into it.
            options.onSelectionChange?.(script.name);
            setEditorValue(script.content, { asBaseline: true });
            editorTitle.textContent = `Editing: ${script.name}`;
            const data = await listSieveScripts(options.storage);
            activeScript = data.active;
            renderScriptList(data.scripts);
        }
        catch (error) {
            options.showStatus(error instanceof Error ? error.message : "Failed to load script", "error");
        }
        finally {
            setBusy(false);
        }
    };
    saveBtn.addEventListener("click", () => {
        if (!selectedScript || loading)
            return;
        void (async () => {
            options.onBeforeSave?.();
            setBusy(true);
            try {
                const name = selectedScript;
                const value = getEditorValue();
                await updateSieveScript(options.storage, name, value);
                content = value;
                markClean();
                await refresh();
                options.showStatus(`Saved ${name}`, "success");
            }
            catch (error) {
                options.showStatus(error instanceof Error ? error.message : "Failed to save script", "error");
            }
            finally {
                setBusy(false);
            }
        })();
    });
    activateBtn.addEventListener("click", () => {
        if (!selectedScript || loading)
            return;
        void (async () => {
            setBusy(true);
            try {
                const name = selectedScript;
                await activateSieveScript(options.storage, name);
                await refresh();
                options.showStatus(`Activated ${name}`, "success");
            }
            catch (error) {
                options.showStatus(error instanceof Error ? error.message : "Failed to activate script", "error");
            }
            finally {
                setBusy(false);
            }
        })();
    });
    deleteBtn.addEventListener("click", () => {
        if (!selectedScript || loading)
            return;
        const name = selectedScript;
        void (async () => {
            const confirmed = await showConfirmDialog(options.root, {
                title: "Delete script?",
                message: `Delete script "${name}"? This cannot be undone.`,
                confirmLabel: "Delete",
                cancelLabel: "Cancel",
                danger: true,
            });
            if (!confirmed)
                return;
            setBusy(true);
            try {
                await deleteSieveScript(options.storage, name);
                clearSelection();
                await refresh();
                options.showStatus(`Deleted ${name}`, "success");
            }
            catch (error) {
                options.showStatus(error instanceof Error ? error.message : "Failed to delete script", "error");
            }
            finally {
                setBusy(false);
            }
        })();
    });
    createBtn.addEventListener("click", () => {
        if (loading)
            return;
        const name = newNameInput.value.trim();
        if (!name) {
            options.showStatus("Enter a script name.", "error");
            return;
        }
        void (async () => {
            if (!(await confirmDiscardIfDirty()))
                return;
            setBusy(true);
            try {
                await createSieveScript(options.storage, name, "");
                newNameInput.value = "";
                await refresh();
                options.showStatus(`Created ${name}`, "success");
                // Force selection even if same name somehow selected
                selectedScript = null;
                await selectScript(name);
            }
            catch (error) {
                options.showStatus(error instanceof Error ? error.message : "Failed to create script", "error");
            }
            finally {
                setBusy(false);
            }
        })();
    });
    updateEditorLock();
    options.onSelectionChange?.(null);
    return {
        element: layout,
        refresh,
        getSelected: () => selectedScript,
        getContent: getEditorValue,
        setContent: (value) => setEditorValue(value),
        setBusy,
        isDirty,
        markDirty,
        markClean,
        confirmDiscardIfDirty,
    };
}
